/**
 * Shared "topmost-first" ordering for stacked pick / snap-hover candidates.
 *
 * AutoCAD-parity selection order (the same rule `SpatialIndex.pick` applies):
 * a higher `zOrder` draws on top, so it sorts FIRST; on a `zOrder` tie the
 * smaller-bbox-area entity wins, because a tight entity nested inside a large
 * one is the more specific pick.
 *
 * Extracted (MC4 S3) from the byte-identical comparators in
 * `scene/StagePointerHandlers.ts` (S2 pick / drag stacked candidates) and
 * `snap/pointerSnapStep.ts` (S3 snap / hover stacked candidates) — one domain
 * truth, both paths consume it. THREE-free leaf utility (imports nothing), so
 * it adds no dependency-graph edges back into the scene/snap modules.
 */

/** Minimal structural shape the comparator needs (a `RenderableEntity` satisfies it). */
export interface OrderableCandidate {
  readonly zOrder: number;
  readonly bounds: {
    readonly minX: number;
    readonly maxX: number;
    readonly minY: number;
    readonly maxY: number;
  };
}

/**
 * Comparator for `Array.prototype.sort`: orders candidates topmost-first
 * (zOrder DESC, then smallest-bbox-area ASC on a zOrder tie).
 */
export function compareTopmostFirst(
  a: OrderableCandidate,
  b: OrderableCandidate,
): number {
  if (a.zOrder !== b.zOrder) return b.zOrder - a.zOrder;
  const aArea =
    Math.max(0, a.bounds.maxX - a.bounds.minX) *
    Math.max(0, a.bounds.maxY - a.bounds.minY);
  const bArea =
    Math.max(0, b.bounds.maxX - b.bounds.minX) *
    Math.max(0, b.bounds.maxY - b.bounds.minY);
  return aArea - bArea;
}
